/** @jsxImportSource react */
import React, { useState } from 'react'
import { useTranslation } from '../i18n'
import { useAuth } from '../contexts/auth'
import { UserRole } from '../types'
import { Navigate, useNavigate } from 'react-router-dom'
import {
  Users,
  Percent as PercentIcon,
  Bell,
  ChevronRight,
  X,
} from 'lucide-react'

const Settings = () => {
  const { t, isRtl } = useTranslation()
  const { user } = useAuth()
  const navigate = useNavigate()
  const [isCommissionModalOpen, setIsCommissionModalOpen] = useState(false)
  if (user?.role !== UserRole.Admin) {
    return <Navigate to="/dashboard" />
  }
  const settingsCategories = [
    {
      id: 'account',
      title: t('accountSettings'),
      items: [
        {
          id: 'users',
          title: t('userManagement'),
          description:
            t('userManagementDesc'),
          icon: <Users size={20} />,
          onClick: () => navigate('/users'),
          badge: null,
        },
      ],
    },
    {
      id: 'business',
      title: t('businessSettings'),
      items: [
        {
          id: 'commission',
          title: t('commissionRates'),
          description:
            t('commissionRatesDesc'),
          icon: <PercentIcon size={20} />,
          onClick: () => setIsCommissionModalOpen(true),
          badge: null,
        },
      ],
    },
    {
      id: 'notifications',
      title: t('notificationSettings'),
      items: [
        {
          id: 'notificationPrefs',
          title: t('notificationPreferences'),
          description:
            t('notificationPreferencesDesc'),
          icon: <Bell size={20} />,
          onClick: () => navigate('/notification-preferences'),
          badge: null,
        },
      ],
    },
  ]

  return (
    <div className="w-full bg-white min-h-screen">
      <div className="container mx-auto">
        <div className="mb-8 bg-white p-6 rounded-xl shadow-sm border border-slate-200/70 animate-fade-in">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6">
            <div>
              <h1 className="text-2xl font-bold text-slate-900 mb-2">
                {t('settings')}
              </h1>
              <p className="text-slate-600 max-w-2xl">
                {t('settingsDesc')}
              </p>
            </div>
          </div>
          {settingsCategories.map((category) => (
            <div key={category.id} className="mb-8">
              <h2 className="text-lg font-semibold text-slate-800 mb-4 flex items-center">
                {category.title}
                <div className="h-px flex-grow bg-slate-200 ms-4"></div>
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {category.items.map((item) => (
                  <div
                    key={item.id}
                    onClick={item.onClick}
                    className="bg-white border border-slate-200 rounded-xl p-6 transition-all duration-200 hover:shadow-md hover:border-primary-300 cursor-pointer group relative overflow-hidden"
                  >
                    <div className="absolute top-0 right-0 w-24 h-24 bg-gradient-to-bl from-slate-50 to-transparent rounded-full -mt-12 -mr-12 opacity-80"></div>
                    <div className="flex items-start gap-4">
                      <div className="p-2.5 bg-primary-50 text-primary-600 rounded-lg group-hover:bg-primary-100 transition-colors">
                        {item.icon}
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center justify-between">
                          <h3 className="font-semibold text-lg text-slate-800 group-hover:text-primary-600 transition-colors mb-2 flex items-center">
                            {item.title}
                            {item.badge && (
                              <span className="ml-2 px-2 py-0.5 bg-primary-100 text-primary-700 text-xs rounded-full font-medium">
                                {item.badge}
                              </span>
                            )}
                          </h3>
                          <ChevronRight className={`w-5 h-5 text-slate-400 group-hover:text-primary-500 group-hover:translate-x-1 rtl:group-hover:-translate-x-1 transition-all ${isRtl ? 'rotate-180' : ''}`} />
                        </div>
                        <p className="text-sm text-slate-600">
                          {item.description}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
      {/* Commission Modal */}
      {isCommissionModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm animate-fade-in">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md relative overflow-hidden animate-fade-up">
            <div className="absolute -top-32 -left-32 w-64 h-64 bg-primary-500/10 rounded-full mix-blend-multiply filter blur-3xl opacity-50"></div>
            <div className="absolute -bottom-32 -right-32 w-64 h-64 bg-secondary-500/10 rounded-full mix-blend-multiply filter blur-3xl opacity-50"></div>
            <div className="relative z-10">
              <div className="flex justify-between items-center border-b border-slate-100 p-5">
                <h3 className="text-xl font-bold text-slate-900">
                  {t('featureInDevelopment')}
                </h3>
                <button
                  onClick={() => setIsCommissionModalOpen(false)}
                  className="text-slate-500 hover:text-slate-700 transition-colors focus:outline-none"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
              <div className="p-6">
                <p className="text-slate-600 mb-6">
                  {t('commissionRatesComingSoon')}
                </p>
                <button
                  onClick={() => setIsCommissionModalOpen(false)}
                  className="w-full py-3 bg-primary-500 hover:bg-primary-600 text-white rounded-lg font-medium transition-colors"
                >
                  {t('close')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
export default Settings